import { motion } from "framer-motion";
import { Check, Crown, ArrowRight, Sparkles } from "lucide-react";
import { PACKAGES } from "@/data/catalog";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Packages({ onBook }) {
  const pkgs = PACKAGES.filter((p) => !["hemozy-plus", "hemozy-pro"].includes(p.id));
  const memberships = PACKAGES.filter((p) => ["hemozy-plus", "hemozy-pro"].includes(p.id));

  return (
    <section id="packages" className="bg-[#F8FAFC] dot-grid-dark py-20 lg:py-28" data-testid="packages-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="flex items-center gap-4 mb-6">
          <span className="font-mono-med text-sm text-[#D92638] border border-[#D92638]/40 rounded-full px-3.5 py-1">05</span>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500">Health packages</span>
          <span className="h-px flex-1 bg-slate-200" />
        </motion.div>

        <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
          className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-2xl leading-tight">
          Preventive care, bundled and priced for Vizag families.
        </motion.h2>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {pkgs.map((p, i) => (
            <motion.div key={p.id} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.08 }}
              data-testid={`package-card-${p.id}`}
              className={`relative bg-white rounded-[1.75rem] border p-7 flex flex-col hover:-translate-y-1.5 transition-all duration-400 hover:shadow-[0_16px_40px_-8px_rgba(15,25,44,0.15)] ${p.tag === "Most Popular" ? "border-[#D92638]/50 ring-1 ring-[#D92638]/20" : "border-slate-200/90"}`}>
              <span className={`self-start text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full ${p.tag === "Most Popular" ? "bg-[#D92638] text-white" : "bg-[#0D9488]/10 text-[#0D9488]"}`}>{p.tag}</span>
              <h3 className="mt-4 text-xl font-bold text-[#0B192C]">{p.name}</h3>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="font-mono-med text-3xl font-bold text-[#0B192C]">₹{p.price.toLocaleString("en-IN")}</span>
                <span className="text-xs text-slate-500">/ person</span>
              </div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              <div className="mt-4 bg-[#F8FAFC] border border-slate-200/80 rounded-xl px-4 py-3">
                <div className="text-[10px] font-bold tracking-widest uppercase text-slate-400 mb-1">Includes</div>
                <div className="text-xs text-slate-600 leading-relaxed">{p.includes}</div>
              </div>
              <button onClick={() => onBook([p.id])} data-testid={`package-book-${p.id}`}
                className={`mt-6 flex items-center justify-center gap-2 font-semibold text-sm px-5 py-3 rounded-full transition-all duration-200 active:scale-95 ${p.tag === "Most Popular" ? "bg-[#D92638] hover:bg-[#B81D2D] text-white" : "border border-[#0B192C] text-[#0B192C] hover:bg-[#0B192C] hover:text-white"}`}>
                Book this package <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Memberships: Hemozy+ & Hemozy Pro */}
        <div className="mt-8 grid md:grid-cols-2 gap-5" data-testid="memberships-row">
          {memberships.map((m, i) => {
            const isPro = m.id === "hemozy-pro";
            const perks = isPro
              ? ["3x full-body check-ups / yr", "Unlimited priority teleconsults", "Faster doctor callbacks", "Priority home collection"]
              : ["2x full-body check-ups / yr", "Unlimited report teleconsults", "Priority home collection"];
            return (
              <motion.div key={m.id} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.1 }}
                className={`relative overflow-hidden rounded-[1.75rem] bg-[#0B192C] grain p-8 lg:p-10 flex flex-col ${isPro ? "ring-2 ring-[#D92638]" : ""}`}
                data-testid={isPro ? "hemozy-pro-card" : "hemozy-plus-card"}>
                <div className="absolute inset-0 dot-grid opacity-50" />
                <div className="relative flex-1 flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <Crown className={`w-5 h-5 ${isPro ? "text-[#D92638]" : "text-[#14B8A6]"}`} />
                      <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#14B8A6]">{isPro ? "Hemozy Pro" : "Hemozy+"} Membership</span>
                    </div>
                    {isPro && <span className="bg-[#D92638] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">Best value</span>}
                  </div>
                  <h3 className="mt-4 text-2xl font-bold text-white leading-tight">
                    {isPro ? "The complete family-grade plan." : "A year of health, on autopilot."}
                  </h3>
                  <p className="mt-2.5 text-sm text-slate-300 leading-relaxed">{m.desc}</p>
                  <div className="mt-5 space-y-2.5">
                    {perks.map((t) => (
                      <div key={t} className="flex items-center gap-2.5 text-sm text-slate-200">
                        <Check className="w-4 h-4 text-[#14B8A6] shrink-0" /> {t}
                      </div>
                    ))}
                    {isPro && (
                      <div className="flex items-center gap-2.5 text-sm text-[#14B8A6] font-medium">
                        <Check className="w-4 h-4 shrink-0" /> Add spouse, kids or parents at just ₹2,999/member
                      </div>
                    )}
                  </div>
                  <div className="mt-auto pt-7 flex items-end justify-between gap-4">
                    <div>
                      <div className="font-mono-med text-3xl font-bold text-white">₹{m.price.toLocaleString("en-IN")}<span className="text-base text-slate-400 font-normal">/yr</span></div>
                      <div className="text-xs text-slate-400 mt-1">Less than ₹{Math.round(m.price / 365)} a day</div>
                    </div>
                    <button onClick={() => onBook([m.id])} data-testid={isPro ? "hemozy-pro-join-button" : "hemozy-plus-join-button"}
                      className={`shrink-0 flex items-center gap-2 font-semibold px-6 py-3.5 rounded-full transition-all duration-300 hover:-translate-y-0.5 active:scale-95 ${isPro ? "bg-[#D92638] hover:bg-[#B81D2D] text-white shadow-[0_0_25px_rgba(217,38,56,0.3)]" : "bg-white text-[#0B192C] hover:bg-[#14B8A6] hover:text-[#0B192C]"}`}>
                      <Sparkles className="w-4 h-4" /> Join {isPro ? "Pro" : "Hemozy+"}
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
